import type { Role } from '@/api/types';
import { useAuthStore } from './authStore';

/** Single source of UI permissions; keep in sync with @PreAuthorize rules on the backend. */
export const PERMISSIONS = {
  'customers.edit': ['TENANT_ADMIN', 'COMPLIANCE_OFFICER', 'ANALYST'],
  'customers.delete': ['TENANT_ADMIN', 'COMPLIANCE_OFFICER'],
  'screening.run': ['TENANT_ADMIN', 'COMPLIANCE_OFFICER', 'ANALYST'],
  'config.view': ['TENANT_ADMIN', 'COMPLIANCE_OFFICER'],
  'config.edit': ['TENANT_ADMIN', 'COMPLIANCE_OFFICER'],
  'dataFeeds.view': ['TENANT_ADMIN', 'COMPLIANCE_OFFICER', 'PLATFORM_ADMIN'],
  'dataFeeds.manage': ['PLATFORM_ADMIN'],
  'audit.view': ['TENANT_ADMIN', 'COMPLIANCE_OFFICER'],
  'users.manage': ['TENANT_ADMIN'],
  'platform.manage': ['PLATFORM_ADMIN'],
} as const satisfies Record<string, readonly Role[]>;

export type Permission = keyof typeof PERMISSIONS;

export function hasPermission(roles: readonly Role[] | undefined, permission: Permission): boolean {
  const allowed: readonly Role[] = PERMISSIONS[permission];
  return !!roles?.some((r) => allowed.includes(r));
}

export function usePermissions() {
  const roles = useAuthStore((s) => s.user?.roles);
  return { can: (permission: Permission) => hasPermission(roles, permission) };
}
